import { api } from './httpClient';
import { API_BASE, getToken } from './httpClient';
import { io } from 'socket.io-client';

let socket = null;
const joinHandlers = new Set();

function socketBaseUrl() {
  if (API_BASE.startsWith('http')) {
    // Dev: use CRA proxy (same origin) so socket.io works reliably on localhost:3000
    if (process.env.NODE_ENV === 'development' && API_BASE.includes('localhost')) {
      return window.location.origin;
    }
    return API_BASE.replace(/\/api\/v\d+$/, '');
  }
  return window.location.origin;
}

function runJoinHandlers(sock) {
  joinHandlers.forEach((join) => join(sock));
}

export function resetSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function getSocket() {
  const token = getToken();
  if (!socket) {
    socket = io(socketBaseUrl(), {
      auth: { token },
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    socket.on('connect', () => runJoinHandlers(socket));
  } else if (token) {
    socket.auth = { token };
    if (!socket.connected) socket.connect();
  }
  return socket;
}

/** Register a room join that re-runs on every socket connect/reconnect. Returns cleanup. */
export function onSocketJoin(join) {
  const sock = getSocket();
  joinHandlers.add(join);
  if (sock.connected) join(sock);
  return () => joinHandlers.delete(join);
}

export const gamesApi = {
  aviatorState: () => api('/games/aviator/state'),
  aviatorBet: (body) => api('/games/aviator/bet', { method: 'POST', body: JSON.stringify(body) }),
  aviatorCashout: (betId) =>
    api('/games/aviator/cashout', { method: 'POST', body: JSON.stringify({ betId }) }),

  fairAviatorState: (entryFee) => api(`/games/fairaviator/state?entryFee=${entryFee}`),
  fairAviatorBet: (body) =>
    api('/games/fairaviator/bet', { method: 'POST', body: JSON.stringify(body) }),
  fairAviatorCashout: (betId) =>
    api('/games/fairaviator/cashout', { method: 'POST', body: JSON.stringify({ betId }) }),

  wingoState: () => api('/games/wingo/state'),
  wingoBet: (body) => api('/games/wingo/bet', { method: 'POST', body: JSON.stringify(body) }),

  luckyDrawRooms: () => api('/games/luckydraw/rooms'),
  luckyDrawJoin: (roomId) =>
    api('/games/luckydraw/join', { method: 'POST', body: JSON.stringify({ roomId }) }),

  matkaMarkets: () => api('/games/matka/markets'),
  matkaBet: (body) => api('/games/matka/bet', { method: 'POST', body: JSON.stringify(body) }),
  matkaHistory: () => api('/games/matka/history'),

  minesState: () => api('/games/mines/state'),
  minesStart: (body) => api('/games/mines/start', { method: 'POST', body: JSON.stringify(body) }),
  minesReveal: (body) => api('/games/mines/reveal', { method: 'POST', body: JSON.stringify(body) }),
  minesCashout: (sessionId) =>
    api('/games/mines/cashout', { method: 'POST', body: JSON.stringify({ sessionId }) }),

  plinkoState: () => api('/games/plinko/state'),
  plinkoDrop: (body) => api('/games/plinko/drop', { method: 'POST', body: JSON.stringify(body) }),

  limboState: () => api('/games/limbo/state'),
  limboPlay: (body) => api('/games/limbo/play', { method: 'POST', body: JSON.stringify(body) }),

  dragonTigerState: () => api('/games/dragontiger/state'),
  dragonTigerBet: (body) =>
    api('/games/dragontiger/bet', { method: 'POST', body: JSON.stringify(body) }),

  ludoState: () => api('/games/ludo/state'),
  ludoOnline: () => api('/games/ludo/online'),
  ludoSetAvailability: (stake) =>
    api('/games/ludo/availability', { method: 'POST', body: JSON.stringify({ stake }) }),
  ludoCreateRoom: (stake) =>
    api('/games/ludo/rooms', { method: 'POST', body: JSON.stringify({ stake }) }),
  ludoCreateBotGame: () => api('/games/ludo/bot', { method: 'POST', body: JSON.stringify({}) }),
  ludoJoin: (body) => api('/games/ludo/join', { method: 'POST', body: JSON.stringify(body) }),
  ludoLeave: (roomId) =>
    api('/games/ludo/leave', { method: 'POST', body: JSON.stringify({ roomId }) }),
  ludoGetRoom: (roomId) => api(`/games/ludo/rooms/${roomId}`),
  ludoInvite: (toUserId, stake) =>
    api('/games/ludo/invite', { method: 'POST', body: JSON.stringify({ toUserId, stake }) }),
  ludoRespondInvite: (inviteId, accept) =>
    api('/games/ludo/invite/respond', {
      method: 'POST',
      body: JSON.stringify({ inviteId, accept }),
    }),
  ludoRoll: (roomId) =>
    api('/games/ludo/roll', { method: 'POST', body: JSON.stringify({ roomId }) }),
  ludoMove: (roomId, tokenIndex) =>
    api('/games/ludo/move', { method: 'POST', body: JSON.stringify({ roomId, tokenIndex }) }),
  ludoSync: (roomId) =>
    api('/games/ludo/sync', { method: 'POST', body: JSON.stringify({ roomId }) }),
};
